import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import moment from 'moment';

import { IcDeleteTrash, IcEdit, IcStar } from '@/assets/images';
import {
  BaseAdditionalInfo,
  IAdditionalInfoItemProps,
} from '@/components/CustomForms/AdditionalInfoForm/types';
import { DATE_TIME_FORMAT } from '@/config';
import { useUserStore } from '@/store';
import colors from '@/theme/variables/colors';
import { formatPersonName } from '@/utils/masterName';

export const AdditionalInfoItem = <T extends BaseAdditionalInfo>({
  additionalInfo,
  updateAdditionalInfos,
  onEdit,
  createdAtFormat,
  hideActions,
  mentionUsers,
}: IAdditionalInfoItemProps<T>) => {
  const theme = useTheme();
  const { user } = useUserStore();
  const isCurrentUser = user?._id === additionalInfo?.createdBy?._id;
  const isDark = theme.palette.mode === 'dark';

  const otherUserBubbleBg = isDark
    ? theme.palette.background.default
    : '#f1f1f1';
  const otherUserBubbleColor = isDark ? theme.palette.text.primary : '#000';
  const otherUserMetaColor = isDark ? theme.palette.text.secondary : '#666';
  const avatarBg = theme.palette.background.paper;
  const avatarBorder = theme.palette.divider;
  const dispatcherColor = theme.palette.error.main;
  const otherUserAvatarColor = isDark
    ? theme.palette.text.secondary
    : '#647787';

  const removeMessage = async () => {
    additionalInfo.isDeleted = true;
    additionalInfo.deletedAt = new Date().toISOString();
    if (updateAdditionalInfos) updateAdditionalInfos(additionalInfo);
  };

  // Check if message starts with * and extract the actual message
  const isStarred = additionalInfo.description?.startsWith('*');
  const displayDescription = isStarred
    ? additionalInfo.description.substring(1).trim()
    : additionalInfo.description;

  const renderDescriptionWithMentions = (text?: string) => {
    if (!text) return null;
    const tokens: Array<{ token: string; label: string }> = [];
    if (mentionUsers?.length) {
      mentionUsers.forEach((userItem) => {
        const username = userItem.username || userItem.email?.split('@')[0];
        // Canonical display order is 'Last First Middle' (what the input now
        // inserts), but stored texts predating the flip contain 'First Last' —
        // register a token for BOTH so legacy mentions keep highlighting.
        const displayName = formatPersonName(userItem);
        const legacyName = `${userItem.firstName || ''} ${
          userItem.lastName || ''
        }`.trim();
        const label = displayName || userItem.fullName || username || '';
        if (!label) return;
        tokens.push({ token: `@${label}`, label: `@${label}` });
        if (legacyName && legacyName !== label) {
          // Legacy tokens keep the STORED text as their label: rewriting it to
          // the new order would make the read view disagree with the raw text
          // shown the moment the author clicks Edit.
          tokens.push({ token: `@${legacyName}`, label: `@${legacyName}` });
        }
        if (username) {
          tokens.push({ token: `@${username}`, label: `@${label}` });
        }
      });
    }
    if (!tokens.length) return text;
    tokens.sort((a, b) => b.token.length - a.token.length);
    const parts: Array<string | JSX.Element> = [];
    let index = 0;
    while (index < text.length) {
      let match: { token: string; label: string } | undefined;
      for (const item of tokens) {
        if (text.startsWith(item.token, index)) {
          match = item;
          break;
        }
      }
      if (match) {
        parts.push(
          <span
            key={`${index}-${match.token}`}
            style={{
              color: colors.warning.main,
              fontWeight: 500,
            }}
          >
            {match.label}
          </span>,
        );
        index += match.token.length;
      } else {
        parts.push(text[index]);
        index += 1;
      }
    }
    return parts;
  };

  return (
    <Box
      className={`item ${isCurrentUser ? 'current-user' : ''}`}
      sx={{
        width: '100%',
        padding: isCurrentUser ? '0px 8px' : '0px 0px',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{
          maxWidth: isCurrentUser ? '80%' : '100%',
          marginLeft: isCurrentUser ? 'auto' : '0',
        }}
      >
        {!isCurrentUser && (
          <Tooltip
            title={
              <Typography sx={{ whiteSpace: 'pre-line' }}>
                {formatPersonName(additionalInfo.createdBy)}
              </Typography>
            }
          >
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: avatarBg,
                color: additionalInfo.createdBy?.roles?.some(
                  (r) => r.role === 'DISPATCHER',
                )
                  ? dispatcherColor
                  : otherUserAvatarColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                marginRight: '8px',
                border: '1px solid',
                borderColor: avatarBorder,
                fontWeight: 600,
              }}
            >
              {additionalInfo.createdBy?.firstName?.charAt(0) || ''}
              {additionalInfo.createdBy?.lastName?.charAt(0) || ''}
            </Box>
          </Tooltip>
        )}

        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            backgroundColor: isCurrentUser
              ? colors.grey[60]
              : otherUserBubbleBg,
            color: isCurrentUser ? '#fff' : otherUserBubbleColor,
            borderRadius: '6px',
            padding: '12px 16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            {isStarred && <IcStar />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="div"
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  wordBreak: 'break-word',
                  textDecoration: additionalInfo?.isDeleted
                    ? 'line-through'
                    : 'none',
                  marginBottom: '4px',
                }}
              >
                {renderDescriptionWithMentions(displayDescription)}
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip
                    title={moment(additionalInfo.createdAt).format(
                      DATE_TIME_FORMAT,
                    )}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: isCurrentUser
                          ? 'rgba(255,255,255,0.7)'
                          : otherUserMetaColor,
                      }}
                    >
                      {moment(additionalInfo.createdAt).format(
                        createdAtFormat || 'HH:mm',
                      )}
                    </Typography>
                  </Tooltip>
                  {additionalInfo.isEdited && !additionalInfo.isDeleted && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: isCurrentUser
                          ? 'rgba(255,255,255,0.7)'
                          : otherUserMetaColor,
                      }}
                    >
                      Edited
                    </Typography>
                  )}
                  {additionalInfo.isDeleted && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: isCurrentUser
                          ? 'rgba(255,255,255,0.7)'
                          : otherUserMetaColor,
                      }}
                    >
                      Deleted
                    </Typography>
                  )}
                </Stack>

                {!hideActions &&
                  !additionalInfo?.isDeleted &&
                  isCurrentUser && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        '& svg': {
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          color: isCurrentUser ? '#fff' : otherUserMetaColor,
                        },
                      }}
                    >
                      <Tooltip title="Delete">
                        <IcDeleteTrash onClick={removeMessage} />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IcEdit
                          onClick={() => {
                            if (onEdit) {
                              onEdit(additionalInfo);
                            }
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
